import { CONVEYANCE_TYPES, type Location } from '@koala/sdk';
import { addDays, format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { DATE_FORMAT } from '@/constants/dates';
import { formatDate } from '@/utils/dates';
import { TIME_MODES } from '@/constants/global';
import { getStoreTimeInfo, isStoreOpenOnDay } from '@/utils/locations';

/** @TODO Update this to use the days data from the location endpoint instead of generating days
 * @param input.canOrderAhead Does the location support ordering for future days?
 * @param input.advanceDays The number of days in advance an order can be made for.
 * @param input.leadDays Minimum lead time in days before the earliest orderable date.
 */
export function getAvailableDays(location: Location, conveyanceType: string | undefined) {
  if (!location) return [{ label: '', value: '', disabled: true }];
  const input = {
    canOrderAhead: location.order_ahead,
    advanceDays: location.order_ahead_days,
    leadDays: location.cached_data?.advance_notice_days ?? 0,
  };
  const { hasRemainingHoursToday, storeDate } = getStoreTimeInfo(location, conveyanceType);
  // Anchor the day list on the store's local date rather than the diner's device date.
  // The backend cutoff is store-anchored (coo-api LocationMenuController: now(store tz) +
  // order_ahead_days, end of day, enforced by a `before:` rule), so a diner whose device
  // had rolled past midnight was offered a date the menu request then rejected with HTTP
  // 400 `failures.wanted-at`. Deriving from `storeDate` also keeps the shift-today guard
  // below comparing like with like — both sides come from this one value. Note the two
  // coincide when the location carries no timezone at all, since `storeDate` then falls
  // back to the device's date. (TEAM-14372)
  const storeToday = parseISO(storeDate);
  // If ordering ahead is enabled, create an array of orderable future days.
  const availableDays = input.canOrderAhead
    ? Array.from({ length: Math.max(input.advanceDays - input.leadDays + 1, 0) }, (_, i) => {
        const day = addDays(storeToday, i + input.leadDays);
        const timeOption = {
          label: format(day, DATE_FORMAT.LABEL),
          value: format(day, DATE_FORMAT.YEAR_MONTH_DAY_DASHED),
          disabled: false,
        };
        timeOption.disabled = !isStoreOpenOnDay(timeOption, location);
        return timeOption;
      })
    : [
        {
          label: format(storeToday, DATE_FORMAT.LABEL),
          value: format(storeToday, DATE_FORMAT.YEAR_MONTH_DAY_DASHED),
          disabled: false,
        },
      ];

  // Remove today if there are no remaining hours today
  // availableDays may be empty when leadDays > order_ahead_days
  if (!hasRemainingHoursToday && availableDays[0]?.value === storeDate) {
    availableDays.shift();
  }
  return availableDays;
}

export const getTimeSelectOption = (storeTimezone: string) => (time: string) => {
  if (time === TIME_MODES.ASAP || !time) {
    return {
      label: '',
      value: time,
    };
  }

  const timeSlotInTimezone = utcToZonedTime(time, storeTimezone);
  return {
    label: `${formatDate(timeSlotInTimezone, DATE_FORMAT.HOURS_WITH_MINUTES)}`,
    value: time,
  };
};
/*
 * Conveyance modes are types using multiple Enum values
 * which are difficult to pass around in the code, so we map them
 * all to CONVEYANCE_TYPES. These enums should eventually be reconciled
 * and use `as const` instead of an Enum, which work poorly in JavaScript
 */
export function getMappedConveyanceType(type: string) {
  switch (type) {
    case 'delivery':
      return CONVEYANCE_TYPES.DELIVERY;
    case 'Delivery':
      return CONVEYANCE_TYPES.DELIVERY;
    case 'pick_up':
      return CONVEYANCE_TYPES.PICKUP;
    case 'Pickup':
      return CONVEYANCE_TYPES.PICKUP;
    case 'curbside':
      return CONVEYANCE_TYPES.CURBSIDE;
    case 'Curbside':
      return CONVEYANCE_TYPES.CURBSIDE;
    case 'dine_in':
      return CONVEYANCE_TYPES.DINEIN;
    case 'Dine-In':
      return CONVEYANCE_TYPES.DINEIN;
    case 'drive_thru':
      return CONVEYANCE_TYPES.DRIVETHRU;
    case 'Drive-Thru':
      return CONVEYANCE_TYPES.DRIVETHRU;
    case 'drive_in':
      return CONVEYANCE_TYPES.DRIVEIN;
    case 'Drive-In':
      return CONVEYANCE_TYPES.DRIVEIN;

    default:
      return CONVEYANCE_TYPES.PICKUP;
  }
}
